import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'

type AuthUser = {
  id: string
  email?: string
  name?: string
  picture?: string
  given_name?: string
  user_metadata?: Record<string, any>
}

type SignUpInput = {
  email: string
  password: string
  role: string
  /** Recruiter's chosen school, revalidated server-side against the verified email. */
  schoolId?: string
  emailRedirectTo?: string
}

type AuthContextValue = {
  isAuthenticated: boolean
  isLoading: boolean
  user: AuthUser | null
  session: Session | null
  signInWithPassword: (credentials: { email: string; password: string }) => Promise<void>
  signUp: (input: SignUpInput) => Promise<void>
  signOut: () => Promise<void>
  getAccessToken: () => Promise<string>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

function mapUser(user: User | null): AuthUser | null {
  if (!user) return null
  return {
    id: user.id,
    email: user.email,
    name: (user.user_metadata as any)?.full_name ?? user.email,
    given_name: (user.user_metadata as any)?.full_name?.split(' ')?.[0],
    picture: (user.user_metadata as any)?.avatar_url,
    user_metadata: (user.user_metadata ?? {}) as Record<string, any>,
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let mounted = true

    const initialize = async () => {
      const { data } = await supabase.auth.getSession()
      if (!mounted) return
      setSession(data.session ?? null)
      setUser(mapUser(data.session?.user ?? null))
      setIsLoading(false)
    }

    initialize()

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession ?? null)
      setUser(mapUser(nextSession?.user ?? null))
      setIsLoading(false)
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: !!session?.access_token,
      isLoading,
      user,
      session,
      signInWithPassword: async ({ email, password }) => {
        const { error } = await supabase.auth.signInWithPassword({ email, password })
        if (error) throw error
      },
      signUp: async ({ email, password, role, schoolId, emailRedirectTo }) => {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo,
            data: { role, ...(schoolId ? { schoolId } : {}) },
          },
        })
        if (error) throw error
      },
      signOut: async () => {
        const { error } = await supabase.auth.signOut()
        if (error) throw error
      },
      getAccessToken: async () => {
        const { data } = await supabase.auth.getSession()
        const token = data.session?.access_token
        if (!token) throw new Error('No active session')
        return token
      },
    }),
    [isLoading, session, user]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
